import { useCustomNavigate } from '@hooks/useCustomNavigate'
import { LoadingPage } from '@pages/loading-page'
import { useAuthStore } from '@store/authStore'
import { getShopifyParams } from '@utils/getShopifyParams'
import type React from 'react'
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

const appBackend = import.meta.env.VITE_BACKEND_BASE_URL

export const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { token, isLoading, isHydrated, authFailure } = useAuthStore()
  const { shop } = getShopifyParams()
  const customNavigate = useCustomNavigate()
  const location = useLocation()

  const params = new URLSearchParams(window.location.search)
  const embedded = params.get('embedded') === '1'
  const shouldUseAppBridgeLogin = embedded && Boolean(shop)

  useEffect(() => {
    if (!isHydrated || isLoading || token) {
      return
    }
    if (!shouldUseAppBridgeLogin) {
      return
    }
    customNavigate({
      url: `${appBackend}/login?shop=${shop}&top_level=true`,
      target: 'self',
    })
  }, [isHydrated, isLoading, token, shop, shouldUseAppBridgeLogin, customNavigate])

  // Wait for store to rehydrate from localStorage
  if (!isHydrated) {
    return <LoadingPage title="Initializing..." />
  }

  // If we're actively loading a new token, show loading
  if (isLoading) {
    return <LoadingPage title="Authenticating..." />
  }

  if (authFailure) {
    return children
  }

  // If no token after hydration and not loading, redirect to install
  if (!token) {
    if (shouldUseAppBridgeLogin) {
      return <LoadingPage title="Redirecting..." />
    }
    return <Navigate to={{ pathname: '/auth/install', search: location.search }} replace />
  }

  return children
}
